import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { ProTrackMark, StackLogo } from "./ProTrackLogo";
import { Clock, CheckCircle, Zap, Shield, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const PLANS = [
  { id: "basic",        name: "Basic",        price: 15000, color: "#4f8ef7",
    features: ["50 tenders/month", "Federal Govt sources", "Email alerts", "1 user"] },
  { id: "professional", name: "Professional", price: 35000, color: "#00d4a1", popular: true,
    features: ["Unlimited tenders", "All 38 sources", "WhatsApp + Email alerts", "AI matching", "5 users"] },
  { id: "enterprise",   name: "Enterprise",   price: 75000, color: "#ffd166",
    features: ["Everything in Pro", "Bid generator (AI)", "Compliance hub", "Unlimited users"] },
  { id: "elite",        name: "Elite",          price: 160000, color: "#a78bfa", elite: true,
    features: ["Everything in Enterprise", "Win Probability Engine", "Auto Bid Assistant (AI)", "Competitor Intelligence", "Dedicated account manager"] },
];

export default function PaymentWall({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role) localStorage.setItem("user_role", user.role);
    // Admins have permanent access — never call payments/status for them
    if (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") {
      setSubStatus({ status: "active", plan: "super_admin" });
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("access_token");
    if (!token) { setLoading(false); return; }
    // Through the shared API client, not a raw fetch: a 401 is answered by its interceptor - a
    // silent refresh and retry, or clearing the session and going to /login (for example a session
    // for an account that still owes its email code). A raw fetch ignored the status code, so a
    // rejected session showed up here as "your free trial has ended".
    api.get("/payments/status")
      .then(res => {
        const data = res.data;
        setSubStatus(data);
        // Store for PlanGate to read anywhere without prop drilling
        localStorage.setItem("sub_status", JSON.stringify(data));
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) return;   // the interceptor is already sending the user to /login
        setSubStatus({ status: "active" });
        setLoading(false);
      });
  }, [user?.role]);

  if (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN") {
    localStorage.setItem("sub_status", JSON.stringify({ status: "active", plan: "super_admin" }));
    localStorage.setItem("user_role", user.role);
    return children;
  }

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#080d1a", color: "rgba(232,234,240,0.5)", fontSize: "14px", gap: "10px" }}>
      <div style={{ width: "20px", height: "20px", border: "2px solid rgba(0,212,161,0.2)", borderTopColor: "#00d4a1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      Checking access...
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (subStatus?.status === "active" || subStatus?.status === "trial") return children;

  const handlePay = (plan) => {
    const token = localStorage.getItem("access_token");
    if (!token || !user?.email) { navigate("/login"); return; }
    if (typeof window.PaystackPop === "undefined") {
      alert("Payment system loading. Refresh and try again.");
      return;
    }
    const reference = `PTN_${plan.id.toUpperCase()}_${Date.now()}`;
    const handler = window.PaystackPop.setup({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: user.email,
      amount: plan.price * 100,
      currency: "NGN",
      ref: reference,
      metadata: { plan_id: plan.id, user_id: user.id },
      callback: async (response) => {
        try {
          const res = await fetch(`${API}/payments/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ reference: response.reference, plan_id: plan.id }),
          });
          const data = await res.json();
          if (data.success) setSubStatus({ status: "active", plan: plan.id });
          else alert("Payment received but activation failed. Contact support with ref: " + response.reference);
        } catch {
          alert("Verification error. Contact support with ref: " + response.reference);
        }
      },
      onClose: () => {},
    });
    handler.openIframe();
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080d1a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Serif+Display&display=swap');*{box-sizing:border-box}.wp:hover{transform:translateY(-4px)!important}`}</style>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "40px", cursor: "pointer" }} onClick={() => navigate("/")}>
        <ProTrackMark size={28} />
        <span style={{ fontSize: "18px", fontWeight: "800", color: "#fff" }}>ProTrack<span style={{ color: "#00d4a1" }}>NG</span></span>
      </div>

      <div style={{ textAlign: "center", marginBottom: "48px", maxWidth: "520px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "5px 14px", background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: "100px", fontSize: "11px", fontWeight: "700", color: "#ff6b6b", marginBottom: "20px", letterSpacing: "0.5px" }}>
          <Clock size={11} /> YOUR FREE TRIAL HAS ENDED
        </div>
        <h1 style={{ fontSize: "clamp(28px,5vw,44px)", fontWeight: "900", color: "#fff", letterSpacing: "-1px", fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: "14px", lineHeight: "1.1" }}>
          Choose a plan to<br /><span style={{ color: "#00d4a1" }}>continue</span>
        </h1>
        <p style={{ fontSize: "15px", color: "rgba(232,234,240,0.45)", lineHeight: "1.7" }}>
          Your 14-day free trial has ended. Subscribe to keep tracking Nigerian tenders.
        </p>
      </div>

      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center", marginBottom: "48px", maxWidth: "900px", width: "100%" }}>
        {PLANS.map(plan => (
          <div key={plan.id} className="wp" style={{ flex: "1 1 240px", maxWidth: "280px", background: plan.popular ? "linear-gradient(135deg,rgba(0,212,161,0.07),rgba(0,212,161,0.02))" : "rgba(232,234,240,0.02)", border: plan.popular ? "1px solid rgba(0,212,161,0.3)" : "1px solid rgba(232,234,240,0.08)", borderRadius: "18px", padding: "28px 24px", position: "relative", transition: "transform 0.2s" }}>
            {plan.popular && <div style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", background: "#00d4a1", color: "#080d1a", fontSize: "10px", fontWeight: "800", padding: "4px 14px", borderRadius: "100px", whiteSpace: "nowrap" }}>MOST POPULAR</div>}
            <div style={{ fontSize: "11px", fontWeight: "700", color: plan.color, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "10px" }}>{plan.name}</div>
            <div style={{ marginBottom: "20px" }}>
              <span style={{ fontSize: "34px", fontWeight: "900", color: plan.color }}>₦{plan.price / 1000}k</span>
              <span style={{ fontSize: "13px", color: "rgba(232,234,240,0.4)" }}>/month</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
              {plan.features.map(f => (
                <div key={f} style={{ display: "flex", gap: "8px", fontSize: "13px", color: "rgba(232,234,240,0.65)" }}>
                  <CheckCircle size={13} color={plan.color} style={{ flexShrink: 0, marginTop: "2px" }} />{f}
                </div>
              ))}
            </div>
            <button onClick={() => handlePay(plan)} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: plan.popular ? "none" : `1px solid ${plan.color}44`, background: plan.popular ? "linear-gradient(135deg,#00d4a1,#00b888)" : "transparent", color: plan.popular ? "#080d1a" : plan.color, fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit" }}>
              Subscribe — ₦{plan.price.toLocaleString()}/mo
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", justifyContent: "center", marginBottom: "40px" }}>
        {[[Zap,"38 live sources"],[Shield,"Compliance tracking"],[TrendingUp,"AI tender matching"]].map(([Icon, label]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "rgba(232,234,240,0.35)" }}>
            <Icon size={13} color="#00d4a1" />{label}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "11px", color: "rgba(232,234,240,0.25)" }}>Powered by</span>
        <StackLogo size={14} variant="white" />
        <span style={{ fontSize: "11px", color: "rgba(232,234,240,0.3)", fontWeight: "600" }}>ProStack NG Technologies</span>
      </div>
    </div>
  );
}
